import { normalize } from "normalizr";
import * as schemaExpenses from "~/schemas/schema.expenses.jsx";
import { arrayOfExpenses } from "~/schemas/schema.expenses.jsx";
import * as schemaProjects from "~/schemas/schema.projects.jsx";
import * as schemaTasks from "~/schemas/schema.tasks.jsx";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie.jsx";
import { fetch } from "~/utils/fetch.jsx";
import { encodeData } from "~/utils/utils.jsx";

export const fetchProjectExpenses = (projectId, params = {}) => {
  const query = encodeData(params);
  return {
    types: [
      actionTypes.FETCH_PROJECT_EXPENSES_LOADING,
      actionTypes.FETCH_PROJECT_EXPENSES_SUCCESS,
      actionTypes.FETCH_PROJECT_EXPENSES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses/for-screen?${query}`,
        withHeaders: true,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        expenses: schemaExpenses.arrayOfExpenses,
      },
      cursor: params?.cursor,
    },
  };
};

export const fetchExpensesForTask = (projectId, taskId) => {
  return {
    types: [actionTypes.FETCH_EXPENSES_LOADING, actionTypes.FETCH_EXPENSES_SUCCESS, actionTypes.FETCH_EXPENSES_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/tasks/${taskId}/expenses`,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.project,
          Task: schemaTasks.task,
        },
        expenses: schemaExpenses.arrayOfExpenses,
      },
    },
  };
};

export const addExpenseForTask = (projectId, attributes) => {
  return {
    types: [actionTypes.ADD_EXPENSE_LOADING, actionTypes.ADD_EXPENSE_SUCCESS, actionTypes.ADD_EXPENSE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses`,
        method: `POST`,
        body: attributes,
      }),
    payload: {
      attributes,
      schema: {
        single: schemaExpenses.expense,
      },
    },
  };
};

export const fetchExpense = (projectId, expenseId) => {
  return {
    types: [actionTypes.FETCH_EXPENSE_LOADING, actionTypes.FETCH_EXPENSE_SUCCESS, actionTypes.FETCH_EXPENSE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses/${expenseId}`,
        method: `GET`,
      }),
    payload: {
      projectId,
      expenseId,
      schema: {
        single: schemaExpenses.expense,
      },
    },
  };
};

export const editExpense = (projectId, expenseId, attributes) => {
  return {
    types: [actionTypes.EDIT_EXPENSE_LOADING, actionTypes.EDIT_EXPENSE_SUCCESS, actionTypes.EDIT_EXPENSE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses/${expenseId}`,
        method: `PUT`,
        body: attributes,
      }),
    payload: {
      attributes,
      schema: {
        single: schemaExpenses.expense,
      },
    },
  };
};

export const moveExpenseToTrash = (id) => {
  return {
    types: [
      actionTypes.MOVE_EXPENSE_TO_TRASH_LOADING,
      actionTypes.MOVE_EXPENSE_TO_TRASH_SUCCESS,
      actionTypes.MOVE_EXPENSE_TO_TRASH_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/move-to-trash/expense/${id}`,
        method: `PUT`,
      }),
    payload: {
      schema: {
        single: schemaExpenses.expense,
      },
    },
  };
};

const expenseFromEventStream = (data = {}, type) => {
  return {
    type,
    response: {
      single: data,
    },
    schema: {
      single: schemaExpenses.expense,
    },
  };
};

export const expenseCreatedFromEventStream = (data) => {
  return expenseFromEventStream(data, actionTypes.EXPENSE_CREATED_FROM_EVENT_STREAM);
};

export const expenseUpdatedFromEventStream = (data) => {
  return expenseFromEventStream(data, actionTypes.EXPENSE_UPDATED_FROM_EVENT_STREAM);
};

export const expenseMovedToTrashFromEventStream = (data) => {
  return expenseFromEventStream(data, actionTypes.EXPENSE_MOVE_TO_TRASH_FROM_EVENT_STREAM);
};

export const expenseRestoredFromTrashFromEventStream = (data) => {
  return expenseFromEventStream(data, actionTypes.EXPENSE_RESTORED_FROM_TRASH_FROM_EVENT_STREAM);
};

export const expenseMovedToProjectFromEventStream = (data) => {
  return expenseFromEventStream(data, actionTypes.EXPENSE_MOVED_TO_PROJECT_FROM_EVENT_STREAM);
};

export const moveExpense = (projectId, id, attributes) => {
  return {
    types: [actionTypes.MOVE_EXPENSE_LOADING, actionTypes.MOVE_EXPENSE_SUCCESS, actionTypes.MOVE_EXPENSE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses/${id}/move`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      projectId,
      id,
      ...attributes,
      schema: {
        single: schemaExpenses.expense,
      },
    },
  };
};

export const fetchParentExpenses = (parentType, parentId, projectId, params = {}) => {
  const query = encodeData({
    location: parentType,
    parent: parentId,
    ...params,
  });

  return {
    types: [actionTypes.FETCH_EXPENSES_LOADING, actionTypes.FETCH_EXPENSES_SUCCESS, actionTypes.FETCH_EXPENSES_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses/for-screen?${query}`,
        withHeaders: true,
      }),
    payload: {
      parentType,
      parentId,
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        expenses: schemaExpenses.arrayOfExpenses,
      },
    },
  };
};

export const batchEditExpenses = (projectId, ids, attributes) => {
  return {
    types: [
      actionTypes.BATCH_EDIT_EXPENSES_LOADING,
      actionTypes.BATCH_EDIT_EXPENSES_SUCCESS,
      actionTypes.BATCH_EDIT_EXPENSES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/expenses`,
        method: "PUT",
        body: { ids, attributes },
      }),
    payload: {
      projectId,
      ids,
      ...attributes,
      schema: schemaExpenses.arrayOfExpenses,
    },
  };
};

export const updatedExpenses = (data) => {
  return {
    type: actionTypes.UPDATED_EXPENSES,
    response: normalize(data, arrayOfExpenses),
  };
};
